import { type ErrorEvent, type EventHint } from '@sentry/core'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import _some from 'lodash/some'

import {
  getErrorCode,
  isForbiddenError,
  isNetworkError,
  isUnauthorizedError,
} from '../http/codes'

import {
  findAxiosError,
  findErrorInArgs,
  isCheckoutSessionAxiosError,
  isCheckoutSessionRequestUrl,
} from './sentry.utils'

export const SENTRY_DEVELOPMENT = 'development'
export const SENTRY_PRODUCTION = 'production'
export const SENTRY_SANDBOX = 'sandbox'
export type SentryEnvironment =
  | typeof SENTRY_DEVELOPMENT
  | typeof SENTRY_PRODUCTION
  | typeof SENTRY_SANDBOX

export const SENTRY_IGNORE_ERRORS = ['AuthenticationError']

/** 4xx statuses worth reporting; trim this list as noise patterns become clear. */
export const REPORTABLE_CLIENT_ERROR_STATUSES = [408, 409, 410, 413, 429] as const

export function isReportableClientErrorStatus(status: number | undefined): boolean {
  return _includes(REPORTABLE_CLIENT_ERROR_STATUSES, status)
}

export function getTracesSampleRate(): number {
  const environment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT as SentryEnvironment
  switch (environment) {
    case SENTRY_PRODUCTION:
      return 0.1
    case SENTRY_SANDBOX:
      return 0.5
    case SENTRY_DEVELOPMENT:
      return 1.0
    default:
      return 0.1
  }
}

export function getSentryRelease(): string | undefined {
  return process.env.VERCEL_GIT_COMMIT_SHA || process.env.npm_package_version
}

export function getReplaySessionSampleRate(): number {
  const environment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT as SentryEnvironment
  switch (environment) {
    case SENTRY_PRODUCTION:
      return 0.01
    case SENTRY_SANDBOX:
      return 0.1
    case SENTRY_DEVELOPMENT:
      return 1.0
    default:
      return 0.01
  }
}

function isReportableClientError(error: unknown, args: unknown[] = []): boolean {
  const resolvedError =
    error instanceof Error ? error : (findAxiosError(error, args) ?? findErrorInArgs(args))

  if (isReportableClientErrorStatus(getErrorCode(resolvedError as Error))) {
    return true
  }

  const axiosError = findAxiosError(error, args)
  return axiosError ? isCheckoutSessionAxiosError(axiosError) : false
}

function isReportableClientErrorEvent(event: ErrorEvent): boolean {
  const axiosResponseStatus =
    _get(event, 'contexts.axios.response.status') ?? _get(event, 'extra.axiosResponseStatus')

  if (isReportableClientErrorStatus(axiosResponseStatus as number | undefined)) {
    return true
  }

  const axiosRequestUrl =
    _get(event, 'contexts.axios.request.url') ?? _get(event, 'extra.axiosRequestUrl')

  return isCheckoutSessionRequestUrl(axiosRequestUrl)
}

function isChunkLoadError(event: ErrorEvent): boolean {
  const error = event.exception?.values?.[0]
  return error?.type === 'ChunkLoadError' || _includes(error?.value, 'Loading chunk') || false
}

export function hasStacktrace(event: ErrorEvent): boolean {
  return _some(
    event.exception?.values,
    value => _get(value, 'stacktrace.frames.length', 0) > 0
  )
}

export function shouldReportErrorToSentry(error: unknown, args: unknown[] = []): boolean {
  const resolvedError =
    error instanceof Error ? error : (findAxiosError(error, args) ?? findErrorInArgs(args))

  if (!resolvedError) {
    return true
  }

  if (isUnauthorizedError(resolvedError) || isForbiddenError(resolvedError)) {
    return false
  }

  if (isNetworkError(resolvedError)) {
    return false
  }

  const status = getErrorCode(resolvedError)
  if (status && status >= 400 && status < 500) {
    return isReportableClientError(resolvedError, args)
  }

  return true
}

export function beforeSend(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  if (isChunkLoadError(event)) {
    return null
  }

  const originalException = hint?.originalException
  if (originalException && !shouldReportErrorToSentry(originalException)) {
    return null
  }

  const axiosResponseStatus =
    _get(event, 'contexts.axios.response.status') ?? _get(event, 'extra.axiosResponseStatus')
  if (
    typeof axiosResponseStatus === 'number' &&
    axiosResponseStatus >= 400 &&
    axiosResponseStatus < 500 &&
    !isReportableClientErrorEvent(event)
  ) {
    return null
  }

  if (!hasStacktrace(event)) {
    const exceptionValue = event.exception?.values?.[0]
    const originalName =
      originalException instanceof Error ? originalException.name : exceptionValue?.type
    const originalMessage =
      originalException instanceof Error ? originalException.message : exceptionValue?.value

    event.extra = {
      ...event.extra,
      captureSiteStack: new Error(
        `Missing stacktrace for ${originalName ?? 'Error'}: ${originalMessage ?? 'unknown'}`
      ).stack,
    }
    event.tags = {
      ...event.tags,
      missing_stacktrace: 'true',
    }
  }

  return event
}
